// components/KanbanBoard.tsx
import React from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Card, Tag, Typography, Avatar } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { Candidate, Status } from "../types";

const { Text } = Typography;

const COLUMNS: Status[] = [
  "Applied",
  "Screening",
  "Interview",
  "Offer",
  "Hired",
  "Rejected",
];

interface KanbanBoardProps {
  candidates: Candidate[];
  onStatusChange: (id: string, newStatus: Status) => void;
  onView: (candidate: Candidate) => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  candidates,
  onStatusChange,
  onView,
}) => {
  const handleDragEnd = (result: DropResult) => {
    const { destination, draggableId } = result;
    if (!destination) return;
    if (
      destination.droppableId === result.source.droppableId &&
      destination.index === result.source.index
    ) {
      return;
    }
    const newStatus = destination.droppableId as Status;
    onStatusChange(draggableId, newStatus);
  };

  // Modern subtle colors for column headers
  const getHeaderColor = (status: Status) => {
    switch (status) {
      case "Applied":
        return "#3b82f6"; // Blue
      case "Screening":
        return "#f59e0b"; // Amber
      case "Interview":
        return "#8b5cf6"; // Violet
      case "Offer":
        return "#06b6d4"; // Cyan
      case "Hired":
        return "#10b981"; // Emerald
      case "Rejected":
        return "#ef4444"; // Red
      default:
        return "#64748b";
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div
        style={{
          display: "flex",
          overflowX: "auto",
          gap: 20,
          paddingBottom: 20,
          alignItems: "flex-start",
          height: "calc(100vh - 200px)",
        }}
      >
        {COLUMNS.map((status) => {
          const columnCandidates = candidates.filter(
            (c) => c.status === status
          );
          const headerColor = getHeaderColor(status);

          return (
            <Droppable key={status} droppableId={status}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    background: snapshot.isDraggingOver ? "#f1f5f9" : "#f8fafc",
                    padding: "16px 12px",
                    borderRadius: 12,
                    width: 300,
                    minWidth: 300,
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "100%",
                  }}
                >
                  {/* Column Header */}
                  <div
                    style={{
                      marginBottom: 16,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0 4px",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: headerColor,
                        }}
                      />
                      <Text strong style={{ fontSize: 14 }}>
                        {status}
                      </Text>
                    </div>
                    <Tag
                      style={{
                        marginRight: 0,
                        borderRadius: 10,
                        border: "none",
                        background: "#e2e8f0",
                        fontWeight: 600,
                      }}
                    >
                      {columnCandidates.length}
                    </Tag>
                  </div>

                  {/* Cards Area */}
                  <div style={{ overflowY: "auto", flex: 1, padding: "0 4px" }}>
                    {columnCandidates.map((candidate, index) => (
                      <Draggable
                        key={candidate.id}
                        draggableId={candidate.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              marginBottom: 12,
                            }}
                          >
                            <Card
                              size="small"
                              bordered={false}
                              onClick={() => onView(candidate)}
                              style={{
                                borderRadius: 8,
                                boxShadow: snapshot.isDragging
                                  ? "0 10px 15px -3px rgba(0,0,0,0.1)"
                                  : "0 1px 3px 0 rgba(0,0,0,0.1)",
                                cursor: "grab",
                                border: "1px solid #f1f5f9",
                              }}
                              bodyStyle={{ padding: 12 }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginBottom: 8,
                                }}
                              >
                                <Text strong style={{ fontSize: 14 }}>
                                  {candidate.name}
                                </Text>
                                <Avatar
                                  size={24}
                                  style={{
                                    backgroundColor: headerColor,
                                    fontSize: 12,
                                  }}
                                >
                                  {candidate.name.charAt(0)}
                                </Avatar>
                              </div>

                              <Tag
                                color="blue"
                                style={{
                                  marginBottom: 8,
                                  border: "none",
                                  background: "#eff6ff",
                                  color: "#3b82f6",
                                }}
                              >
                                {candidate.role}
                              </Tag>

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  color: "#94a3b8",
                                  fontSize: 12,
                                }}
                              >
                                <ClockCircleOutlined />
                                <span>{candidate.appliedDate}</span>
                              </div>
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </div>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
};
